import { forwardRef, ReactNode, useEffect, useId, useMemo, useState } from "react";
import { Skeleton, Typography, SvgIcon, RadioGroup } from "@mui/material";
import { CoreButton, CoreModal, CoreRadio, coreToast } from "@verity/components/core";
import { changeTimeOnly } from "../../helpers/time";
import {
	isLiveSessionRegistrationActive,
	isLiveSessionRegistrationEnded,
} from "../../portal/components/assessments-page/student-page/filter";
import i18n from "../../i18n";
import styles from "./LiveSessionSelector.module.css";
import CheckCircle from "../../images/check-circle2.svg?react";
import Calendar from "../images/date.svg?react";
import Time from "../images/time.svg?react";
import Locked from "../images/locked.svg?react";

type LiveSession = {
	id: string;
	name: string;
	startTime: string;
	endTime: string;
	registrationStartTime?: string;
	registrationEndTime?: string;
	createdAt: string;
};

function formatDate(dateString: string) {
	return new Date(dateString).toLocaleDateString([], { month: "short", day: "numeric" });
}

function formatTime(dateString: string) {
	return new Date(dateString).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function Section({ icon, title, children }: { icon: ReactNode; title: ReactNode; children: ReactNode }) {
	return (
		<div className={styles.section}>
			<div className={styles.sectionHeader}>
				<SvgIcon>{icon}</SvgIcon>
				<Typography variant="h3" className={styles.sectionTitle}>
					{title}
				</Typography>
			</div>
			{children}
		</div>
	);
}

type OptionButtonStyle = "filled" | "future" | "radio";

const OptionButton = forwardRef<
	HTMLButtonElement,
	{
		disabled?: boolean;
		style?: OptionButtonStyle;
		value: string;
		title: ReactNode;
		children: ReactNode;
	}
>(function OptionButton({ disabled = false, style = "filled", value, title, children, ...radioProps }, ref) {
	const radioButtonId = useId();
	return style === "filled" || style === "future" ? (
		<div>
			<input
				{...radioProps}
				id={radioButtonId}
				className={styles.hiddenRadioButton}
				type="radio"
				value={value}
				disabled={disabled}
			/>
			<label
				className={`${styles.filledOptionButton} ${style === "future" ? styles.future : ""}`}
				htmlFor={radioButtonId}
			>
				<div className={styles.optionButtonTitle}>{title}</div>
				{children}
			</label>
		</div>
	) : (
		<label className={styles.radioOptionButton} htmlFor={radioButtonId}>
			<div className={styles.optionButtonTitle}>
				{title}
				<CoreRadio
					{...radioProps}
					id={radioButtonId}
					forceTabIndex={false}
					value={value}
					disabled={disabled}
					sx={{ padding: 0 }}
					ref={ref}
				/>
			</div>
			{children}
		</label>
	);
});

function OptionButtonSkeleton() {
	return <Skeleton variant="rounded" className={styles.optionButtonSkeleton} />;
}

function SessionButton({
	groupName,
	selectedSessionId,
	initialSelectedSessionId,
	session,
	currTimestamp,
	...radioProps
}: {
	groupName: string;
	selectedSessionId?: string;
	initialSelectedSessionId?: string;
	session: LiveSession;
	currTimestamp?: number;
}) {
	const { id, name, startTime, endTime, registrationStartTime, registrationEndTime, createdAt } = session;

	const currentRegistration = id === initialSelectedSessionId;
	const registrationActive = isLiveSessionRegistrationActive(session, currTimestamp);
	const registrationPast = isLiveSessionRegistrationEnded(session, currTimestamp);
	const registrationNotStarted = !registrationActive && !registrationPast;

	const displayedStartTime = registrationStartTime ?? createdAt;
	const displayedEndTime = registrationEndTime ?? endTime;
	const registrationTimeText = (
		<>
			{formatDate(displayedStartTime)}, {formatTime(displayedStartTime)} - {formatDate(displayedEndTime)},{" "}
			{formatTime(displayedEndTime)}
		</>
	);
	let sessionInfo: ReactNode;
	if (registrationNotStarted) {
		sessionInfo = (
			<>
				<div>{i18n.t("live.registerTSX.LiveSessionSelectorTSX.registrationWindow")}</div>
				{registrationTimeText}
			</>
		);
	} else if (currentRegistration) {
		sessionInfo = (
			<>
				<div>{i18n.t("live.registerTSX.LiveSessionSelectorTSX.confirmedRegistration")}</div>
				<div className={`${styles.registrationTime} ${styles.current}`}>
					<SvgIcon>
						<CheckCircle />
					</SvgIcon>
					{i18n.t("live.registerTSX.LiveSessionSelectorTSX.registeredSession")}
				</div>
			</>
		);
	} else {
		sessionInfo = (
			<>
				<div>{i18n.t("live.registerTSX.LiveSessionSelectorTSX.registrationWindow")}</div>
				<div className={styles.registrationTime}>{registrationTimeText}</div>
			</>
		);
	}

	return (
		<OptionButton
			{...radioProps}
			key={id}
			value={id}
			disabled={registrationNotStarted}
			style={registrationNotStarted ? "future" : "radio"}
			title={name}
		>
			<div className={styles.sessionDetails}>
				{formatTime(startTime)} - {formatTime(endTime)}
				{<div className={styles.registrationText}>{sessionInfo}</div>}
			</div>
		</OptionButton>
	);
}

function ButtonGrid({
	value,
	onChange,
	children,
}: {
	value?: string;
	onChange?: (id?: string) => void;
	children: ReactNode;
}) {
	return (
		<RadioGroup
			className={styles.buttonGrid}
			value={value ?? null}
			onChange={(e) => {
				if (e.target instanceof HTMLInputElement) {
					onChange?.(e.target.value);
				}
			}}
		>
			{children}
		</RadioGroup>
	);
}

function SessionsSection({
	sessions,
	selectedSessionId,
	initialSelectedSessionId,
	onSelect,
	currTimestamp,
}: {
	sessions: LiveSession[];
	selectedSessionId?: string;
	initialSelectedSessionId?: string;
	onSelect: (id?: string) => void;
	currTimestamp?: number;
}) {
	const enabledSessions = sessions.filter(
		(session) =>
			session.id === initialSelectedSessionId ||
			isLiveSessionRegistrationActive(session, currTimestamp) ||
			isLiveSessionRegistrationEnded(session, currTimestamp),
	);
	const disabledFutureSessions = sessions.filter(
		(session) =>
			!isLiveSessionRegistrationActive(session, currTimestamp) &&
			!isLiveSessionRegistrationEnded(session, currTimestamp),
	);

	const sessionFieldName = useId();

	return (
		<>
			{enabledSessions.length > 0 && (
				<Section icon={<Time />} title={i18n.t("live.registerTSX.LiveSessionSelectorTSX.sessions")}>
					<ButtonGrid value={selectedSessionId} onChange={onSelect}>
						{enabledSessions.map((session) => (
							<SessionButton
								key={session.id}
								groupName={sessionFieldName}
								selectedSessionId={selectedSessionId}
								initialSelectedSessionId={initialSelectedSessionId}
								session={session}
								currTimestamp={currTimestamp}
							/>
						))}
					</ButtonGrid>
				</Section>
			)}
			{disabledFutureSessions.length > 0 && (
				<Section icon={<Locked />} title={i18n.t("live.registerTSX.LiveSessionSelectorTSX.futureSessions")}>
					<ButtonGrid value={selectedSessionId}>
						{disabledFutureSessions.map((session) => (
							<SessionButton
								key={session.id}
								groupName={sessionFieldName}
								selectedSessionId={selectedSessionId}
								initialSelectedSessionId={initialSelectedSessionId}
								session={session}
								currTimestamp={currTimestamp}
							/>
						))}
					</ButtonGrid>
				</Section>
			)}
		</>
	);
}

export function LiveSessionSelector({
	sessions,
	isLoading = false,
	initialSelectedSessionId,
	selectedSessionId,
	onSelect,
	currTimestamp,
}: {
	sessions: LiveSession[];
	isLoading?: boolean;
	initialSelectedSessionId?: string;
	selectedSessionId?: string;
	onSelect: (id?: string) => void;
	currTimestamp?: number;
}) {
	const sessionsByDates = useMemo(() => {
		const visibleSessions = sessions.filter(
			(session) => !isLiveSessionRegistrationEnded(session, currTimestamp) || session.id === initialSelectedSessionId,
		);
		const groupedSessions: Record<number, LiveSession[]> = {};
		visibleSessions.forEach((session) => {
			const startDateTimestamp = changeTimeOnly(new Date(session.startTime), 0, 0).getTime();
			if (!groupedSessions[startDateTimestamp]) {
				groupedSessions[startDateTimestamp] = [];
			}
			groupedSessions[startDateTimestamp].push(session);
		});
		return groupedSessions;
	}, [sessions, initialSelectedSessionId, currTimestamp]);

	const selectedSession = selectedSessionId ? sessions.find(({ id }) => id === selectedSessionId) : undefined;
	const [selectedDate, setSelectedDate] = useState<number | undefined>(undefined);
	useEffect(() => {
		if (selectedSession) {
			setSelectedDate(changeTimeOnly(new Date(selectedSession.startTime), 0, 0).getTime());
		}
	}, [selectedSession]);

	useEffect(() => {
		if (selectedDate && !(selectedDate in sessionsByDates)) {
			setSelectedDate(undefined);
		}
	}, [sessionsByDates]);

	return (
		<div className={styles.sessionSelector}>
			<Section icon={<Calendar />} title={i18n.t("live.registerTSX.LiveSessionSelectorTSX.selectDate")}>
				<ButtonGrid
					value={selectedDate ? String(selectedDate) : undefined}
					onChange={(date) => {
						setSelectedDate(Number(date));
						onSelect(undefined);
					}}
				>
					{isLoading && (
						<>
							<OptionButtonSkeleton />
							<OptionButtonSkeleton />
							<OptionButtonSkeleton />
							<OptionButtonSkeleton />
						</>
					)}
					{!isLoading &&
						Object.keys(sessionsByDates).map((dateTimestamp, index) => (
							<OptionButton
								style="radio"
								key={dateTimestamp}
								value={dateTimestamp}
								title={i18n.t("live.registerTSX.LiveSessionSelectorTSX.dateNumber", { number: index + 1 })}
							>
								{new Date(Number(dateTimestamp)).toLocaleDateString()}
							</OptionButton>
						))}
				</ButtonGrid>
			</Section>
			{selectedDate && (
				<>
					<div className={styles.divider} />
					<SessionsSection
						sessions={sessionsByDates[selectedDate] ?? []}
						selectedSessionId={selectedSessionId}
						initialSelectedSessionId={initialSelectedSessionId}
						onSelect={onSelect}
					/>
				</>
			)}
		</div>
	);
}

function PastSessionChangeConfirmationModal({ onClose, onConfirm }: { onClose: () => void; onConfirm: () => void }) {
	return (
		<CoreModal
			open
			onClose={onClose}
			title={i18n.t("live.registerTSX.LiveSessionSelectorTSX.confirmRegistration")}
			buttons={
				<>
					<CoreButton color="secondary" onClick={onClose}>
						{i18n.t("live.registerTSX.LiveSessionSelectorTSX.cancel")}
					</CoreButton>
					<CoreButton
						onClick={() => {
							onConfirm();
							onClose();
						}}
					>
						{i18n.t("live.registerTSX.LiveSessionSelectorTSX.updateRegistration")}
					</CoreButton>
				</>
			}
			maxWidth="sm"
		>
			{i18n.t("live.registerTSX.LiveSessionSelectorTSX.confirmRegistrationText")}
		</CoreModal>
	);
}

export default function LiveSessionSelectorPageContent({
	sessions,
	isLoading,
	initialSelectedSessionId,
	onSave,
	saveButtonLabel = i18n.t("live.registerTSX.LiveSessionSelectorTSX.register"),
	onCancel,
	currTimestamp,
}: {
	sessions: LiveSession[];
	isLoading?: boolean;
	initialSelectedSessionId?: string;
	onSave: (selectedSessionId: string) => Promise<void>;
	saveButtonLabel?: ReactNode;
	onCancel?: () => void;
	currTimestamp?: number;
}) {
	const firstSessionId = sessions[0]?.id;
	const [selectedSessionId, setSelectedSessionId] = useState<string | undefined>(
		initialSelectedSessionId ?? firstSessionId,
	);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!isLoading) {
			setSelectedSessionId(initialSelectedSessionId ?? firstSessionId);
		}
	}, [isLoading, initialSelectedSessionId]);

	const [confirmationModalShowing, setConfirmationModalShowing] = useState(false);

	const register = async () => {
		if (selectedSessionId) {
			setSaving(true);
			try {
				await onSave(selectedSessionId);
			} catch (e) {
				console.error(e);
				coreToast.error(i18n.t("live.registerTSX.LiveSessionSelectorTSX.registerError"));
			} finally {
				setSaving(false);
			}
		}
	};

	return (
		<div className={styles.content}>
			<LiveSessionSelector
				sessions={sessions}
				isLoading={isLoading}
				initialSelectedSessionId={initialSelectedSessionId}
				selectedSessionId={selectedSessionId}
				onSelect={setSelectedSessionId}
				currTimestamp={currTimestamp}
			/>
			<div className={styles.registerButtonRow}>
				{onCancel && (
					<CoreButton size="medium" color="secondary" className={styles.registerButton} onClick={onCancel}>
						{i18n.t("live.registerTSX.LiveSessionSelectorTSX.cancel")}
					</CoreButton>
				)}
				<CoreButton
					size="medium"
					className={styles.registerButton}
					loading={saving}
					onClick={async () => {
						const initialSelectedSession = initialSelectedSessionId
							? sessions.find(({ id }) => id === initialSelectedSessionId)
							: undefined;
						if (initialSelectedSession && isLiveSessionRegistrationEnded(initialSelectedSession)) {
							setConfirmationModalShowing(true);
						} else {
							await register();
						}
					}}
					disabled={!selectedSessionId || selectedSessionId === initialSelectedSessionId}
					disabledTooltip={
						!selectedSessionId
							? i18n.t("live.registerTSX.LiveSessionSelectorTSX.selectionRequired")
							: i18n.t("live.registerTSX.LiveSessionSelectorTSX.alreadySelected")
					}
					slotProps={{ tooltipWrapper: { style: { maxWidth: "100%" } } }}
				>
					{saveButtonLabel}
				</CoreButton>
			</div>
			{confirmationModalShowing && (
				<PastSessionChangeConfirmationModal onClose={() => setConfirmationModalShowing(false)} onConfirm={register} />
			)}
		</div>
	);
}
